import { NUMERIC_ORDER_STATUS, type NumericOrderStatus } from 'js/Constants';
import { omitNil } from 'js/mylib/Utils';
import { authenticated, authenticated_cached } from './WebRequest';
import type {
  ApiThunk,
  CloseLocalorderParameters,
  CloseOrderitemParameters,
  ColourCodeFreeParameters,
  Customer,
  CustomerSearchParameters,
  CustomerSearchResponse,
  DeleteCustomerResponse,
  DeleteLocalorderParameters,
  DeleteLocalorderResponse,
  Dispense,
  HideOrderitemParameters,
  Localorder,
  MoveOrdersData,
  MoveOrdersResponse,
  MoveOrdersToCustomerParameters,
  Nullable,
  Orderitem,
  OrderitemsIncrementalParameters,
  OrderitemsIncrementalResponse,
  QueueOrder,
  SaveLocalorderResponse,
  SaveOrderitemResponse,
} from './types/Order';

export type {
  ApiThunk,
  CloseLocalorderParameters,
  CloseOrderitemParameters,
  ColourCodeFreeParameters,
  Cntinitem,
  Customer,
  CustomerSearchParameters,
  CustomerSearchResponse,
  DeleteCustomerResponse,
  DeleteLocalorderParameters,
  DeleteLocalorderResponse,
  Dispense,
  HideOrderitemParameters,
  HistoryOrderitem,
  HistorySeek,
  Localorder,
  MoveOrdersData,
  MoveOrdersResponse,
  MoveOrdersToCustomerParameters,
  Nullable,
  Orderitem,
  OrderitemsIncrementalParameters,
  OrderitemsIncrementalResponse,
  QueueOrder,
  QueueOrderitem,
  SaveLocalorderResponse,
  SaveOrderitemResponse,
} from './types/Order';

export function history_search_orderitems(
  parameters: OrderitemsIncrementalParameters
): ApiThunk<OrderitemsIncrementalResponse> {
  const { siteid, ...params } = omitNil(parameters);
  params.orderstatus = NUMERIC_ORDER_STATUS.ORDER_STATUS_DONE;
  params.include_notes = true;
  return authenticated.get<OrderitemsIncrementalResponse>(
    `/query/site/${siteid}/orderitems_incremental/`,
    {
      params,
    }
  );
}

export function load_last_order(
  parameters: OrderitemsIncrementalParameters
): ApiThunk<OrderitemsIncrementalResponse> {
  const { siteid, ...params } = omitNil(parameters);
  params.limit = 1; // only one is required!
  params.orderstatus = NUMERIC_ORDER_STATUS.ORDER_STATUS_DONE;
  params.local_only = true;
  return authenticated.get<OrderitemsIncrementalResponse>(
    `/query/site/${siteid}/orderitems_incremental/`,
    {
      params,
    }
  );
}

export function search_customers(
  parameters: CustomerSearchParameters
): ApiThunk<CustomerSearchResponse | Customer[]> {
  const { siteid, ...params } = omitNil(parameters);
  // No caching here
  return authenticated.get<CustomerSearchResponse | Customer[]>(
    siteid != null ? `/query/site/${siteid}/customers/` : '/query/customers/',
    { params }
  );
}

export function load_customer(parameters: {
  customerid: number;
}): ApiThunk<Customer> {
  const { customerid } = parameters;
  return authenticated.get<Customer>(`/rest/customer/${customerid}`);
}

export function save_customer(obj: Customer): ApiThunk<Customer> {
  return authenticated.save(
    '/rest/customer/',
    obj,
    'customerid'
  ) as ApiThunk<Customer>;
}

export function delete_customer(
  customer: Customer & { customerid: number; siteid: number }
): ApiThunk<DeleteCustomerResponse> {
  const { customerid, siteid } = customer;
  return authenticated.delete<DeleteCustomerResponse>(
    `/customer/site/${siteid}/delete/${customerid}`
  );
}

export function orders_list(parameters: {
  siteid: number;
}): ApiThunk<QueueOrder[]> {
  const { siteid, ...params } = omitNil(parameters);
  return authenticated.get<QueueOrder[]>(`/query/site/${siteid}/orders_list/`, {
    params,
  });
}

export function orderitem_details(parameters: {
  zoneid: number;
  itemid: number;
}): ApiThunk<Orderitem> {
  const { zoneid, itemid } = parameters;
  return authenticated.get<Orderitem>(
    `/query/zone/${zoneid}/orderitem_details/${itemid}`
  );
}

export function save_localorder(
  obj: Localorder
): ApiThunk<SaveLocalorderResponse> {
  return authenticated.save(
    '/rest/localorder/',
    obj,
    'orderid'
  ) as ApiThunk<SaveLocalorderResponse>;
}

export function save_orderitem(
  obj: Orderitem
): ApiThunk<SaveOrderitemResponse> {
  return authenticated.save(
    '/rest/orderitem/',
    obj,
    'itemid'
  ) as ApiThunk<SaveOrderitemResponse>;
}

export function post_dispense(data: Dispense): ApiThunk<Dispense> {
  return authenticated.post<Dispense>('/rest/dispense/', data);
}

export function delete_localorder(
  parameters: DeleteLocalorderParameters
): ApiThunk<DeleteLocalorderResponse> {
  const { orderid } = parameters;
  return authenticated.delete<DeleteLocalorderResponse>(
    `/rest/localorder/${orderid}`
  );
}

export function close_orderitem(
  parameters: CloseOrderitemParameters
): ApiThunk<string> {
  const { siteid, itemid } = parameters;
  return authenticated.get<string>(`/query/site/${siteid}/orderitem_close`, {
    params: { itemid },
  });
}

export function hide_orderitem(
  parameters: HideOrderitemParameters
): ApiThunk<string> {
  const { siteid, itemid } = parameters;
  return authenticated.post<string>(`/query/site/${siteid}/orderitem_hide`, {
    params: { itemid },
  });
}

export function close_localorder(
  parameters: CloseLocalorderParameters
): ApiThunk<string> {
  const { siteid, orderid } = parameters;
  // same API as for items
  return authenticated.get<string>(`/query/site/${siteid}/orderitem_close`, {
    params: { orderid },
  });
}

export function move_orders(
  siteid: number,
  data: MoveOrdersData
): ApiThunk<MoveOrdersResponse> {
  return authenticated.post<MoveOrdersResponse>(
    `/query/site/${siteid}/move_orders`,
    data
  );
}

export function move_orders_to_customer(
  parameters: MoveOrdersToCustomerParameters
): ApiThunk<{ moved_count: number }> {
  const { siteid, from_customerid, to_customerid, orderids } = parameters;

  return authenticated.post<{ moved_count: number }>(
    `/query/site/${siteid}/move_orders_to_customer`,
    {
      from_customerid,
      to_customerid,
      orderids,
    }
  );
}

export function is_colourcode_free(
  params: ColourCodeFreeParameters
): ApiThunk<boolean> {
  const { zoneid, colourcode } = params;

  return authenticated_cached.get<boolean>(
    `/query/zone/${zoneid}/is_colourcode_free`,
    {
      params: { colourcode },
    }
  );
}
